import { useContext, useEffect, useMemo, useState } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Header from '@components/DesignLibrary/Header';
import ProgressBarLoader from '@/components/ProgressBarLoader';
import { capitalize } from 'lodash';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import Head from 'next/head';

import s from './CreditReportDetails.module.scss';
import CreditReportResponse from './CreditReportResponse';

type Props = {
  creditReportId: string;
};

const CreditReportDetails: React.FC<Props> = ({ creditReportId }) => {
  const [tab, setTab] = useState('main-debtor');
  const {
    data: creditReportResponses,
    fetchingState: { isFetched: fetchedCreditReportResponses },
    getData: getCreditReportResponsesFn } = useNoodleApi(tsClient.creditReportsB2C.getCreditReportResponses);
  const { isInDashboard } = useContext(DashboardContext);

  useEffect(() => {
    getCreditReportResponsesFn({ creditReportId });
  }, [getCreditReportResponsesFn, creditReportId]);

  const responseSlugs: string[] = useMemo(
    () => creditReportResponses?.map(({ slug }: { slug: string }) => slug).sort((a, _) => (a === 'main-debtor' ? -1 : 1)) ?? [],
    [creditReportResponses],
  );

  return (
    <div className={s.wrapper}>
      <Head>
        <title>{isInDashboard ? `Credit Report · Glade Dashboard` : "Credit Report"}</title>
      </Head>
      <Header
        title="Credit Report"
        tabs={responseSlugs.map(slug => ({
          id: slug,
          label: capitalize(slug.replace('-', ' ')),
          onClick: () => setTab(slug),
          selected: slug === tab,
        }))}
      />
      {creditReportResponses
        ? (
          <CreditReportResponse tab={tab} creditReportResponses={creditReportResponses} />
        )
        : (
          <ProgressBarLoader
            chunks={[{
              label: 'Fetching credit report details...',
              loaded: fetchedCreditReportResponses,
            }]}
          />
        )}
    </div>
  );
};

export default CreditReportDetails;
